/**
 * Observability Initialization
 *
 * Initializes the New Relic browser agent (dynamic import to avoid bundle bloat)
 * and configures the LoggerClient singleton. Runs as a module side-effect
 * before React renders.
 */

import type { BrowserAgent } from "@newrelic/browser-agent";

import { environment } from "@constants/environment";
import { loggerClient } from "@utils/logger";

let agentInstance: BrowserAgent | null = null;
let initializationPromise: Promise<BrowserAgent | null> | null = null;

/**
 * Synchronous accessor â€” returns null if initialization has not completed yet.
 */
export const getNewRelicAgent = (): BrowserAgent | null => {
  return agentInstance;
};

/**
 * Asynchronous accessor â€” awaits initialization before returning.
 */
export const getNewRelicAgentAsync = async (): Promise<BrowserAgent | null> => {
  if (initializationPromise) {
    return initializationPromise;
  }
  return agentInstance;
};

const initializeNewRelicAgent = async (): Promise<BrowserAgent | null> => {
  const newRelicConfig = environment.NEW_RELIC;

  if (!newRelicConfig?.enabled) return null;

  const { BrowserAgent } =
    await import("@newrelic/browser-agent/loaders/browser-agent");

  const NR_BEACON = "bam.nr-data.net";

  const options = {
    init: {
      distributed_tracing: { enabled: true },
      performance: { capture_measures: true },
      privacy: { cookies_enabled: true },
      ajax: { deny_list: [NR_BEACON] },
      logging: { enabled: true, autoStart: true }
    },
    loader_config: {
      accountID: newRelicConfig.accountId,
      trustKey: newRelicConfig.trustKey,
      agentID: newRelicConfig.agentID,
      licenseKey: newRelicConfig.licenseKey,
      applicationID: newRelicConfig.applicationId
    },
    info: {
      beacon: NR_BEACON,
      errorBeacon: NR_BEACON,
      licenseKey: newRelicConfig.licenseKey,
      applicationID: newRelicConfig.applicationId,
      sa: 1
    }
  };

  return new BrowserAgent(options);
};

const initializeLogger = () => {
  const loggerConfig = environment.LOGGER;
  if (!loggerConfig?.enabled) return;

  const isLocal = environment.envName === "local";
  const enableConsole = loggerConfig.enableConsole ?? isLocal;

  loggerClient.initialize({
    applicationName: environment.APPLICATION_NAME,
    logSource: "e-invoicing-ui",
    environment: environment.envName,
    enableNewRelic: environment.NEW_RELIC?.enabled ?? false,
    enableConsole: isLocal ? true : enableConsole
  });
};

const initializeObservability = async () => {
  initializationPromise = initializeNewRelicAgent();
  agentInstance = await initializationPromise;
  initializeLogger();
};

initializeObservability();
